import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { forwardRef, useCallback, useMemo } from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import {
    Card,
    CardWrapperButton,
    Description,
    DialogRoot,
    DialogTrigger,
    GitBookLoader,
    Heading,
    Layout,
    Page,
    PageGradientWallpaper,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { siteImport } from '../../../assets';
import {
    applySiteTemplate,
    CreateContentSourceOpenAPIDialog,
    flattenSpacesInOrgAllContent,
    ImportContentDialog,
    SpaceListSelector,
    TemplatePreviewDialog,
    useCreateInOwner,
} from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiClient, apiQuery, apiQueryClient } from '../../../state';
import { extractCollectionNames } from '../../../utils';
import blankPreview from './assets/blank.png';
import blankPreviewDark from './assets/blank-dark.png';
import gitSyncPreview from './assets/gitsync.png';
import gitSyncPreviewDark from './assets/gitsync-dark.png';
import openAPIPreview from './assets/openapi.png';
import openAPIPreviewDark from './assets/openapi-dark.png';
import templatePreview from './assets/template.png';
import templatePreviewDark from './assets/template-dark.png';
import { useSiteScreenLoading } from './SiteLoadingContext';

export function SiteEmptyState(props: {
    site: api.Site;
    siteCustomization: api.SiteCustomizationSettings;
    organization: api.Organization;
}) {
    const { site, siteCustomization, organization } = props;
    const { data: orgAllContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId: organization.id })
    );
    const { isLoading } = useSiteScreenLoading();
    const spaces = useMemo(() => flattenSpacesInOrgAllContent(orgAllContent), [orgAllContent]);
    const addSpaceToSite = useAddSpaceToSite({ organization, site });

    const importV2Feature = useFlag('IMPORT_V2');

    // If there are existing spaces, redirect to the space list
    return (
        <>
            <Page className="flex flex-1 flex-col">
                <PageGradientWallpaper
                    gradientColor={siteCustomization.styling.primaryColor}
                    className="flex-1"
                >
                    <Layout>
                        <main className="flex flex-col gap-14 py-14">
                            <div className="heading-page">
                                <p className="text-base/60">Welcome to your site.</p>
                                <p>Pick how you want to get started</p>
                            </div>
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-12">
                                {importV2Feature.isEnabled ? (
                                    <>
                                        <DocsTemplateAction
                                            organization={organization}
                                            site={site}
                                        />
                                        <ImportAction organization={organization} site={site} />
                                        <BlankSpaceAction organization={organization} site={site} />
                                        <OpenAPIAction organization={organization} site={site} />
                                        <GitSyncAction organization={organization} site={site} />
                                    </>
                                ) : (
                                    <>
                                        <DocsTemplateAction
                                            organization={organization}
                                            site={site}
                                        />
                                        <GitSyncAction organization={organization} site={site} />
                                        <OpenAPIAction organization={organization} site={site} />
                                        <ImportAction organization={organization} site={site} />
                                        <BlankSpaceAction organization={organization} site={site} />
                                    </>
                                )}
                            </div>
                            {spaces.length > 0 ? (
                                <div>
                                    <h2 className="mb-2 text-ui-heading-medium">
                                        Pick from your existing spaces
                                    </h2>
                                    <Card className="p-4">
                                        <SpaceListSelector
                                            spaces={spaces}
                                            collectionNameMap={extractCollectionNames(
                                                orgAllContent
                                            )}
                                            onChange={(space) => {
                                                analytics.logEvent(
                                                    analytics.createSiteEvent(
                                                        {
                                                            id: site.id,
                                                            organization: organization.id,
                                                        },
                                                        {
                                                            type: 'site_content_create_intent',
                                                            source: 'space',
                                                        }
                                                    )
                                                );
                                                addSpaceToSite(space.id);
                                            }}
                                            actionLabel="Use as site content"
                                        />
                                    </Card>
                                </div>
                            ) : null}
                        </main>
                    </Layout>
                </PageGradientWallpaper>
            </Page>
            {isLoading ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-md bg-base/80">
                    <GitBookLoader />
                    <div className="text-ui-heading-small">Setting up your site…</div>
                </div>
            ) : null}
        </>
    );
}

const QuickActionCard = forwardRef(function QuickActionCard(
    props: {
        className?: string;
        image: {
            light: string;
            dark: string;
        };
        title: string;
        description: string;
        onPress?: () => void;
    },
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { className, image, title, description, onPress } = props;

    return (
        <CardWrapperButton
            ref={ref}
            className={clsx(
                '[&:nth-child(-n+2)]:xl:col-span-6 [&:nth-child(-n+2)_img]:aspect-[458/273] [&:nth-child(n+3)]:xl:col-span-4 [&:nth-child(n+3)_img]:aspect-[147/68]',
                className
            )}
            image={{ src: { light: image.light, dark: image.dark } }}
            onPress={onPress}
        >
            <div>
                <Heading>{title}</Heading>
                <Description>{description}</Description>
            </div>
        </CardWrapperButton>
    );
});

/**
 * Create a space in the site.
 */
function useCreateSpaceInSite(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;

    const { createSpace } = useCreateInOwner({ organization: organization.id });
    return useEventCallback(async (options: { title: string; source: string }) => {
        const { data: collection } = await apiClient.collections.createCollection(organization.id, {
            title: site.title,
        });
        assert(createSpace, 'createSpace function is not defined');
        return createSpace({
            withTitle: options.title,
            source: options.source,
            withNavigate: false,
            collectionId: collection.id,
        });
    });
}

/**
 * Add a space to the site.
 */
function useAddSpaceToSite(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const { wrapLoading } = useSiteScreenLoading();

    return useCallback(
        async (spaceId: string) => {
            await wrapLoading(async () => {
                await toast.catch(async () => {
                    await apiClient.siteSpaces.addSpaceToSite(organization.id, site.id, {
                        spaceId,
                    });
                }, 'Error while adding space to site');
                // Force the invalidation of the site structure to be sure the site is updated
                // It's actually invalidated by the `addSpaceToSite` mutation, but for some reason
                // the query is not refetched immediately, so we do it manually here.
                // I suspect a bug when using `useSuspenseQueries` + `invalidateQueries` in parallel.
                await apiQueryClient.invalidateQueries({
                    queryKey: apiQuery('getSiteStructure', {
                        organizationId: organization.id,
                        siteId: site.id,
                    }).queryKey,
                });
            });
        },
        [organization.id, site.id, wrapLoading]
    );
}

/**
 * Apply the docs template to the site.
 */
function DocsTemplateAction(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const { wrapLoading } = useSiteScreenLoading();
    const docsTemplateDialogTrigger = useDialogTriggerState();

    const applyDocsTemplate = async () => {
        // Close the dialog before applying to show the loading state
        docsTemplateDialogTrigger.close();

        await wrapLoading(async () => {
            await applySiteTemplate({
                organizationId: organization.id,
                site,
            });
        });
    };

    return (
        <DialogRoot {...docsTemplateDialogTrigger.rootProps}>
            <DialogTrigger>
                <QuickActionCard
                    image={{
                        light: templatePreview,
                        dark: templatePreviewDark,
                    }}
                    title="Docs template"
                    description="Start with a ready-made documentation layout you can customize."
                    onPress={() => {
                        analytics.logEvent(
                            analytics.createSiteEvent(
                                { id: site.id, organization: organization.id },
                                {
                                    type: 'site_content_create_intent',
                                    source: 'template',
                                    templateKey: 'docs',
                                }
                            )
                        );
                    }}
                />
            </DialogTrigger>

            <TemplatePreviewDialog
                template={{
                    id: 'builtin:ultimate-docs',
                    title: 'Docs template',
                    subtitle:
                        "Take full advantage of GitBook's features to create high-quality documentation",
                    urls: {
                        preview: 'https://gitbook-templates.gitbook.io/site-template',
                    },
                }}
                organizationId={organization.id}
                onApplyTemplate={applyDocsTemplate}
            />
        </DialogRoot>
    );
}

/**
 * Add a GitSync space to the site.
 */
function GitSyncAction(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const { wrapLoading } = useSiteScreenLoading();
    const addSpaceToSite = useAddSpaceToSite({ organization, site });
    const createSpaceInSite = useCreateSpaceInSite({ organization, site });
    const { navigate } = useNavigation();

    const addGitSyncSpaceToSite = useEventCallback(async () => {
        await wrapLoading(async () => {
            const space = await createSpaceInSite({
                title: 'Docs',
                source: 'gitsync',
            });
            await addSpaceToSite(space.id);
            navigate({
                screen: {
                    type: 'space',
                    organization: organization.id,
                    space: space.id,
                    sideSheet: {
                        type: 'gitsync-setup',
                    },
                },
            });
        });
    });

    return (
        <QuickActionCard
            image={{
                light: gitSyncPreview,
                dark: gitSyncPreviewDark,
            }}
            title="Sync with Git"
            description="Keep your docs in sync with your GitHub or GitLab repository."
            onPress={() => {
                analytics.logEvent(
                    analytics.createSiteEvent(
                        { id: site.id, organization: organization.id },
                        {
                            type: 'site_content_create_intent',
                            source: 'import',
                            sourceType: 'gitsync',
                        }
                    )
                );

                void addGitSyncSpaceToSite();
            }}
        />
    );
}

/**
 * Add an OpenAPI space to the site.
 */
function OpenAPIAction(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const { wrapLoading } = useSiteScreenLoading();
    const addSpaceToSite = useAddSpaceToSite({ organization, site });
    const createSpaceInSite = useCreateSpaceInSite({ organization, site });
    const hiveClient = useHiveClient();

    const addOpenAPISpaceToSite = useEventCallback(
        async (computedSource: api.ComputedContentSourceRevisionOpenAPI) => {
            await wrapLoading(async () => {
                const space = await createSpaceInSite({
                    title: 'API Reference',
                    source: 'openapi',
                });
                const branch = hiveClient.sync.branch(space.id, hiveCore.CONTENT_DEFAULT_BRANCH);
                await branch.open();

                const page = hiveCore.createPageComputed({
                    title: 'OpenAPI',
                    computed: computedSource,
                });

                await branch.change((change) => change.insertPage(page));
                await hiveClient.saveBranch(space.id, hiveCore.CONTENT_DEFAULT_BRANCH);

                await addSpaceToSite(space.id);
            });
        }
    );

    return (
        <DialogRoot>
            <DialogTrigger>
                <QuickActionCard
                    image={{
                        light: openAPIPreview,
                        dark: openAPIPreviewDark,
                    }}
                    title="OpenAPI"
                    description="Generate documentation automatically from your OpenAPI spec."
                    onPress={() => {
                        analytics.logEvent(
                            analytics.createSiteEvent(
                                { id: site.id, organization: organization.id },
                                {
                                    type: 'site_content_create_intent',
                                    source: 'import',
                                    sourceType: 'openapi',
                                }
                            )
                        );
                    }}
                />
            </DialogTrigger>

            <CreateContentSourceOpenAPIDialog
                organizationId={organization.id}
                onSubmit={(source) => {
                    void addOpenAPISpaceToSite(source);
                }}
            />
        </DialogRoot>
    );
}

/**
 * Add an import space to the site.
 */
function ImportAction(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const addSpaceToSite = useAddSpaceToSite({ organization, site });
    const importDialogTrigger = useDialogTriggerState();

    return (
        <DialogRoot {...importDialogTrigger.rootProps}>
            <DialogTrigger>
                <QuickActionCard
                    image={siteImport}
                    title="Import"
                    description="Bring in existing content from online docs, Markdown, or HTML files."
                    onPress={() => {
                        analytics.logEvent(
                            analytics.createSiteEvent(
                                { id: site.id, organization: organization.id },
                                {
                                    type: 'site_content_create_intent',
                                    source: 'import',
                                    sourceType: 'other',
                                }
                            )
                        );
                    }}
                />
            </DialogTrigger>
            <ImportContentDialog
                trigger="site_empty_state"
                target={{ site, organization: organization.id }}
                onStartImport={async (target) => {
                    await addSpaceToSite(target.space.id);
                }}
                onComplete={() => {
                    importDialogTrigger.close();
                }}
            />
        </DialogRoot>
    );
}

function BlankSpaceAction(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const { wrapLoading } = useSiteScreenLoading();
    const addSpaceToSite = useAddSpaceToSite({ organization, site });
    const createSpaceInSite = useCreateSpaceInSite({ organization, site });

    const addBlankSpaceToSite = useEventCallback(async () => {
        await wrapLoading(async () => {
            const space = await createSpaceInSite({
                title: 'Docs',
                source: 'blank',
            });
            await addSpaceToSite(space.id);
        });
    });

    return (
        <QuickActionCard
            image={{
                light: blankPreview,
                dark: blankPreviewDark,
            }}
            title="Blank"
            description="Begin with an empty space and create everything from scratch."
            onPress={() => {
                analytics.logEvent(
                    analytics.createSiteEvent(
                        { id: site.id, organization: organization.id },
                        { type: 'site_content_create_intent', source: 'blank' }
                    )
                );
                void addBlankSpaceToSite();
            }}
        />
    );
}
